import { useCallback, useEffect, useRef, useState } from "react";
import type { RenderScanabilityReport, RenderWarning, TemplateSummary, VietQRData } from "../api";
import { getTemplates, renderQr } from "../api";
import StatusMessage from "./StatusMessage";
import TemplateTile, { type TileRenderState } from "./TemplateTile";

const MAX_CONCURRENT_RENDERS = 3;

const PURPOSE_FILTERS: { label: string; category: string }[] = [
  { label: "Tất cả", category: "" },
  { label: "Nhận tiền cá nhân", category: "personal" },
  { label: "Quán cà phê", category: "coffee" },
  { label: "Bán hàng online", category: "retail" },
  { label: "Quán ăn", category: "restaurant" },
  { label: "Cưới hỏi", category: "wedding" },
  { label: "Gen Z vui", category: "genz" },
];

/** Hàng đợi render tối đa 2-3 request song song, chỉ render mẫu đã lọt vào tầm nhìn. */
function useTemplateRenders(qrData: VietQRData) {
  const [states, setStates] = useState<Record<string, TileRenderState>>({});
  const statesRef = useRef(states);
  statesRef.current = states;
  const queueRef = useRef<{ templateId: string; version: number }[]>([]);
  const activeRef = useRef(0);
  const qrDataRef = useRef(qrData);
  qrDataRef.current = qrData;

  const processQueue = useCallback(() => {
    while (activeRef.current < MAX_CONCURRENT_RENDERS && queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      if (!next) break;
      const cur = statesRef.current[next.templateId];
      if (cur && (cur.status === "loading" || cur.status === "done")) continue;

      activeRef.current += 1;
      setStates((s) => ({ ...s, [next.templateId]: { status: "loading" } }));

      renderQr({ templateId: next.templateId, templateVersion: next.version, qrData: qrDataRef.current })
        .then((res) => {
          setStates((s) => ({
            ...s,
            [next.templateId]: {
              status: "done",
              svg: res.svg,
              payload: res.payload,
              warnings: res.warnings,
              scanability: res.scanability,
            },
          }));
        })
        .catch(() => {
          setStates((s) => ({ ...s, [next.templateId]: { status: "error" } }));
        })
        .finally(() => {
          activeRef.current -= 1;
          processQueue();
        });
    }
  }, []);

  const requestRender = useCallback(
    (templateId: string, version: number) => {
      const cur = statesRef.current[templateId];
      if (cur && (cur.status === "loading" || cur.status === "done")) return;
      queueRef.current.push({ templateId, version });
      processQueue();
    },
    [processQueue],
  );

  // Đổi dữ liệu ngân hàng (hiếm khi xảy ra trong luồng này) → làm mới hàng đợi.
  const qrKey = JSON.stringify(qrData);
  useEffect(() => {
    setStates({});
    queueRef.current = [];
    activeRef.current = 0;
  }, [qrKey]);

  return { states, requestRender };
}

type Props = {
  qrData: VietQRData;
  onBack: () => void;
  onSelectTemplate: (
    template: TemplateSummary,
    svg: string,
    payload: string,
    warnings: RenderWarning[],
    scanability: RenderScanabilityReport | null,
  ) => void;
  onUsePhoto: () => void;
};

export default function TemplateStep({
  qrData,
  onBack,
  onSelectTemplate,
  onUsePhoto,
}: Props) {
  const [category, setCategory] = useState("");
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const { states, requestRender } = useTemplateRenders(qrData);

  const load = useCallback((cat: string, cursor: string | null, append: boolean) => {
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setTemplates([]);
      setNextCursor(null);
    }
    setError(null);
    getTemplates({ category: cat || undefined, limit: 20, cursor })
      .then((res) => {
        setTemplates((prev) => (append ? [...prev, ...res.templates] : res.templates));
        setNextCursor(res.nextCursor);
      })
      .catch(() => {
        setError(
        "Chưa tải được danh sách theme. Bạn thử lại giúp mình nhé.",
        );
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, []);

  useEffect(() => {
    load(category, null, false);
  }, [category, load]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !nextCursor || loading || loadingMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) load(category, nextCursor, true);
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [nextCursor, loading, loadingMore, category, load]);

  return (
    <section className="step template-step" aria-labelledby="step-template-title">
      <button type="button" className="link-back" onClick={onBack}>
        ← Quay lại
      </button>

      <h1 id="step-template-title" className="step-title">
        Chọn theme QR có sẵn
      </h1>
      <p className="hint-text small">Chọn một theme có sẵn hoặc chuyển sang tính năng trộn ảnh.</p>

      <button type="button" className="photo-entry-tile" onClick={onUsePhoto}>
        <span className="photo-entry-tile-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
            <circle cx="12" cy="14" r="3.5" />
          </svg>
        </span>
        <span className="photo-entry-tile-text">
          <strong>Dùng ảnh của bạn</strong>
          <span>Đưa mã QR vào ảnh bảng hiệu, tờ giấy, màn hình…</span>
        </span>
        <span className="photo-entry-tile-arrow" aria-hidden="true">→</span>
      </button>

      <div className="purpose-chips" role="list">
        {PURPOSE_FILTERS.map((f) => (
          <button
            key={f.category}
            type="button"
            role="listitem"
            className={`purpose-chip ${category === f.category ? "purpose-chip-active" : ""}`}
            onClick={() => setCategory(f.category)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <StatusMessage tone="loading" text="Đang tải danh sách mẫu…" /> : null}
      {error ? <StatusMessage tone="error" text={error} onRetry={() => load(category, null, false)} /> : null}

      {!loading && !error && templates.length === 0 ? (
        <p className="hint-text">Chưa có mẫu nào cho mục này. Bạn thử mục khác nhé.</p>
      ) : null}

      {!error && templates.length > 0 ? (
        <>
          <div className="template-grid">
            {templates.map((t) => (
              <TemplateTile
                key={t.templateId}
                template={t}
                state={states[t.templateId] ?? { status: "idle" }}
                onVisible={requestRender}
                onSelect={onSelectTemplate}
              />
            ))}
          </div>
          <div ref={sentinelRef} aria-hidden="true" />
          {loadingMore ? <StatusMessage tone="loading" text="Đang tải thêm mẫu…" /> : null}
        </>
      ) : null}
    </section>
  );
}
